import { FormTextareaV2, Typography } from '@/components'
import { AssessmentStepKeys } from '@/enums'
import { SureinAI } from '@/icons/SureinAI'
import { useSegment } from '@/modules'
import { useFormContext, useNextLoading, useOnNextClick } from '@/providers'
import { useRequestLocationsInference } from '@/services/locationTypes'
import { LocationInterfaceResponseType } from '@/types/locationsInference'
import { Badge } from '@surein/ui'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAssessmentValues, useSaveAssessmentStep } from '../helpers'
import { WhyDoWeAsk } from './components/WhyDoWeAsk/WhyDoWeAsk'
import { useFunnelSource } from './hooks/useFunnelSource'

const AILogoWrapper = styled.div`
  text-align: left;
`

const InspirationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0;
  font-size: 15px;
`

const InspirationsBadgesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;

  > div {
    text-wrap: auto;
  }
`

export const BusinessTypeStepAI = ({ categoryId }: { categoryId: string }) => {
  const { values } = useFormContext<any>()
  const { t } = useTranslation()
  const { save } = useSaveAssessmentStep(AssessmentStepKeys.businessTypeAi)
  const requestLocationsInferenceMutation = useRequestLocationsInference()
  const [isLoading, setIsLoading] = useState(false)
  const funnelSource = useFunnelSource(categoryId)
  const analytics = useSegment()
  const { dispatchValues } = useAssessmentValues()

  const saveAndTrack = useCallback(async () => {
    await save()
    setIsLoading(true)
    try {
      await analytics.track('business_type_prompt_submitted', {
        prompt: values.prompt,
      })
      const result = await requestLocationsInferenceMutation.mutateAsync({
        prompt: values.prompt,
        funnelSource,
      })

      let identifiedLocationTypes: string[] = []

      if (result.type === LocationInterfaceResponseType.SUCCESS) {
        identifiedLocationTypes = result.identifiedLocationTypes ?? []

        dispatchValues({
          ['businessTypeAi']: { locationSubtypes: identifiedLocationTypes },
        })

        await analytics.track('business_type_prompt_success', {
          identifiedLocationTypes,
        })
      }

      if (result.type !== LocationInterfaceResponseType.SUCCESS) {
        dispatchValues({
          ['businessTypeAi']: {
            locationSubtypes: [],
            locationSubtypesPromptFailed: true,
          },
        })
        await analytics.track('business_type_prompt_failure')
      }
    } catch {
      dispatchValues({
        ['businessTypeAi']: {
          locationSubtypes: [],
          locationSubtypesPromptFailed: true,
        },
      })
      await analytics.track('business_type_prompt_failure')
    } finally {
      setIsLoading(false)
    }
  }, [values.prompt])

  useNextLoading(() => isLoading, [isLoading])
  useOnNextClick(saveAndTrack)

  return (
    <>
      <FormTextareaV2
        name="prompt"
        placeholder={t('selfAssessment.locationType.aiPromptPlaceholder')}
        disabled={isLoading}
      />

      <AILogoWrapper>
        <SureinAI width={47} />
      </AILogoWrapper>

      <InspirationsWrapper>
        <Typography variant="description">
          {t('selfAssessment.locationType.inspirationsTitle')}
        </Typography>
        <InspirationsBadgesWrapper>
          <Badge isRounded={true}>
            {t('selfAssessment.locationType.inspiration1')}
          </Badge>
          <Badge isRounded={true}>
            {t('selfAssessment.locationType.inspiration2')}
          </Badge>
          <Badge isRounded={true}>
            {t('selfAssessment.locationType.inspiration3')}
          </Badge>
          <Badge isRounded={true}>
            {t('selfAssessment.locationType.inspiration4')}
          </Badge>
        </InspirationsBadgesWrapper>
      </InspirationsWrapper>

      <WhyDoWeAsk />
    </>
  )
}
